import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PolishWithAIDialog } from "@/components/ai/PolishWithAIDialog";
import { cn } from "@/lib/utils";
import {
  CalendarIcon,
  ArrowLeft,
  ImagePlus,
  Clock,
  Search,
  Users,
  Target,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getOpportunities } from "@/lib/queries/catalog";
import { useDebounce } from "@/hooks/use-debounce";
import { useInView } from "react-intersection-observer";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Loader2, X } from "lucide-react";
import { useOnboardingConfig } from "@/components/onboarding/OnboardingSteps";

type Opportunity = {
  id: string;
  name: string;
  type: string;
  beneficiary_types: string[];
  relevant_un_day: string[];
  sdg_alignment: string[];
  community_need: string | null;
  volunteer_support: string | null;
  impact: string | null;
  image_url?: string;
};

type EnumData = {
  data: {
    sdg: Array<{
      id: string;
      value: string;
    }>;
    opportunityType: string[];
    opportunityBeneficiaryType: string[];
  };
};

const formSchema = z.object({
  opportunity_id: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  date: z.date({
    required_error: "Date is required",
  }),
  startTime: z.string().min(1, "Start time is required"),
  duration: z.number().min(0.5, "Duration must be at least 30 minutes"),
  location: z.string().min(1, "Location is required"),
  maxParticipants: z.number().min(1, "Maximum participants is required"),
  prerequisites: z.string().optional(),
  registrationDeadline: z.date({
    required_error: "Registration deadline is required",
  }),
  sdgs: z.array(z.string()).nonempty("Please select at least one SDG"),
  beneficiaryTypes: z
    .array(z.string())
    .min(1, "Please select at least one beneficiary type"),
  eventType: z.string().min(1, "Please select an event type"),
  images: z.array(z.string()).default([]),
  isVirtual: z.boolean().default(false),
});

interface Props {
  session: any;
  profile: any;
}

export default function CreateCommunityEvent({ session, profile }: Props) {
  // State for AI Polish Dialog
  const [showPolishDialog, setShowPolishDialog] = useState(false);
  const [polishFieldLabel, setPolishFieldLabel] = useState<string>("");
  const [polishTargetField, setPolishTargetField] = useState<
    "title" | "description" | "prerequisites" | null
  >(null);
  const [polishOriginalText, setPolishOriginalText] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<Opportunity | null>(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [openBeneficiary, setOpenBeneficiary] = useState(false);
  const [openSDG, setOpenSDG] = useState(false);
  const searchQuery = useDebounce(searchInput, 500);
  const { ref: loadMoreRef, inView } = useInView();
  const { updateConfig } = useOnboardingConfig();
  const LIMIT = 10;

  const { slug } = useParams<{ slug: string }>();
  const isEditMode = !!slug;

  const { data: enumData } = useQuery<EnumData>({
    queryKey: ["enums"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/enums`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch enums");
      }
      return response.json();
    },
  });

  // Fetch event data if in edit mode
  const { data: eventData, isLoading: isLoadingEvent } = useQuery({
    queryKey: ["event", slug],
    queryFn: async () => {
      if (!slug) return null;
      const response = await fetch(
        `${
          import.meta.env.VITE_CC_API_URL
        }/v1/campaign/community-events/${slug}`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch event data");
      }
      return response.json();
    },
    enabled: !!slug && !!session?.access_token,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      date: new Date(),
      startTime: "09:00",
      duration: 1,
      location: "",
      maxParticipants: 1,
      prerequisites: "",
      registrationDeadline: new Date(),
      sdgs: [],
      beneficiaryTypes: [],
      eventType: "",
      isVirtual: false,
      images: [],
    },
    mode: "onChange",
  });

  // Set form values when eventData is loaded
  useEffect(() => {
    if (eventData?.data) {
      const event = eventData.data;

      // Format the time to HH:mm format
      let formattedTime = "09:00";
      if (event.start_time) {
        try {
          // Handle both ISO format (2023-01-01T14:30:00.000Z) and HH:mm:ss format
          const timeString = event.start_time.includes("T")
            ? event.start_time.split("T")[1].substring(0, 5) // Get HH:mm from ISO
            : event.start_time.substring(0, 5); // Get first 5 chars for HH:mm

          // Validate the time format
          if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
            formattedTime = timeString;
          }
        } catch (error) {
          console.error("Error formatting time:", error);
        }
      }

      const formValues = {
        title: event.title || "",
        description: event.description || "",
        date: event.event_date ? new Date(event.event_date) : new Date(),
        startTime: formattedTime,
        duration: event.duration ? Math.floor(event.duration / 60) : 1,
        location: event.format_details || "",
        maxParticipants: event.max_participants || 1,
        prerequisites: Array.isArray(event.prerequisites)
          ? event.prerequisites[0] || ""
          : event.prerequisites || "",
        registrationDeadline: event.deadline
          ? new Date(event.deadline)
          : new Date(),
        sdgs: Array.isArray(event.sdgs) ? event.sdgs : [],
        beneficiaryTypes: Array.isArray(event.beneficiary_types)
          ? event.beneficiary_types
          : [],
        eventType: event.event_type || "",
        isVirtual: event.format === "Virtual",
        images: Array.isArray(event.images) ? event.images : [],
      };

      console.log("Setting form values:", formValues);

      // First reset the form with all values
      form.reset(formValues);

      // Then set the time value again to ensure it's properly set
      if (formattedTime) {
        form.setValue("startTime", formattedTime, { shouldValidate: true });
      }

      if (event.images?.[0]) {
        setPreviewImage(event.images[0]);
      }
    }
  }, [eventData, form]);

  const formState = form.formState;
  const { errors } = formState;

  const {
    data: opportunities,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["opportunities", searchQuery],
    queryFn: async ({ pageParam }) => {
      const response = await getOpportunities(session?.access_token, {
        page: pageParam,
        limit: LIMIT,
        search: searchQuery,
      });
      return {
        data: response.data,
        nextPage: response.data.length === LIMIT ? pageParam + 1 : undefined,
        totalPages: Math.ceil(response.total / LIMIT),
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!session?.access_token,
  });

  const opportunitiesList =
    opportunities?.pages.flatMap((page) => page.data) || [];

  const getFormatIcon = (type?: string) => {
    if (!type) return null;

    switch (type.toLowerCase()) {
      case "volunteer":
        return <Users className="h-4 w-4" />;
      case "donation":
        return <Target className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const matchSDGs = (sdgValues: string[]) => {
    if (!sdgValues?.length) return [];

    return sdgValues
      .map((sdg) => {
        const cleanId = sdg.trim();
        const found = enumData?.data.sdg.find(
          (s) => s.value.toLowerCase() === cleanId.toLowerCase()
        );
        return found?.id || null;
      })
      .filter(Boolean);
  };

  const handleTemplateSelect = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);

    // Convert SDG IDs to their corresponding values
    const sdgValues = matchSDGs(opportunity.sdg_alignment || []);

    // Create a combined description from community_need, volunteer_support, and impact
    const sections = [];

    if (opportunity.community_need) {
      sections.push(`Community Need: ${opportunity.community_need}`);
    }

    if (opportunity.volunteer_support) {
      sections.push(`Volunteer Support: ${opportunity.volunteer_support}`);
    }

    if (opportunity.impact) {
      sections.push(`Impact: ${opportunity.impact}`);
    }

    const combinedDescription =
      sections.length > 0
        ? sections.join("\n\n")
        : opportunity.volunteer_support || "";

    // Get image URL from the template if available
    const imageUrl = opportunity?.image_url || "";

    // Set preview image if available
    if (imageUrl) {
      setPreviewImage(imageUrl);
    }

    form.reset({
      opportunity_id: opportunity.id,
      title: opportunity.name,
      description: combinedDescription,
      sdgs: sdgValues,
      beneficiaryTypes: opportunity.beneficiary_types || [],
      eventType: opportunity.type || "",
      isVirtual: false,
      images: [imageUrl],
    });

    setIsTemplateDialogOpen(false);
  };

  // Format time from 24h to 12h with AM/PM for display
  const formatTimeForDisplay = (time24h: string): string => {
    try {
      const [hours, minutes] = time24h.split(":");
      const hour = parseInt(hours, 10);
      const minute = minutes || "00";
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${displayHour}:${minute} ${period}`;
    } catch (error) {
      console.error("Error formatting time for display:", error);
      return time24h; // Return original if there's an error
    }
  };

  // Function to convert 12-hour time to 24-hour format with timezone offset
  const convertTo24Hour = (time12h: string) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = String(parseInt(hours, 10) + 12);
    }

    // Get timezone offset in hours
    const tzOffset = -new Date().getTimezoneOffset() / 60;
    const tzSign = tzOffset >= 0 ? "+" : "-";
    const tzHours = Math.abs(tzOffset).toString().padStart(2, "0");

    return `${hours}:${minutes}:00${tzSign}00:00`;
  };

  // Get browser's timezone
  const getBrowserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      // Fallback to UTC if timezone detection fails
      return "UTC";
    }
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const requestBody = {
        timezone: getBrowserTimezone(),
        ...(isEditMode && { id: eventData?.data?.id }), // Include ID only in update mode
        opportunity_id: values.opportunity_id || null,
        title: values.title,
        description: values.description || null,
        event_date: format(values.date, "yyyy-MM-dd"),
        start_time: convertTo24Hour(values.startTime),
        duration: values.duration * 60,
        format: values.isVirtual ? "Virtual" : "Outdoor",
        format_details: values.location,
        max_participants: values.maxParticipants,
        prerequisites: values.prerequisites ? [values.prerequisites] : [],
        deadline: format(values.registrationDeadline, "yyyy-MM-dd"),
        sdgs: values.sdgs,
        beneficiary_types: values.beneficiaryTypes,
        event_type: values.eventType,
        is_active: true,
        images: values.images || [],
      };

      // isEditMode is already defined at the component level
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/campaign/community-events`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.message ||
            `Failed to ${isEditMode ? "update" : "create"} community event`
        );
      } else if (!isEditMode) {
        // Only run onboarding config for new events
        updateConfig({ community_event: true });
      }

      toast({
        title: "Success",
        description: `Community event ${
          isEditMode ? "updated" : "created"
        } successfully`,
      });

      navigate("/dashboard/community-events");
    } catch (error) {
      console.error("Error creating community event:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create community event. Please try again.",
      });
    }
  }

  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/upload/image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const { data } = await response.json();
      const currentImages = form.getValues("images") || [];
      form.setValue("images", [...currentImages, data.url]);
      setPreviewImage(data.url);

      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload image. Please try again.",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // Function to check form validity
  const checkFormValidity = async () => {
    const isValid = await form.trigger();
    return isValid;
  };

  // Function to handle submit button click
  const handleSubmitButtonClick = async () => {
    const isValid = await checkFormValidity();
    if (isValid) {
      form.handleSubmit(onSubmit)();
    } else {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields correctly.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="h-10 w-10 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              {isEditMode ? "Edit" : "Create"} Community Event
            </h2>
            <p className="text-muted-foreground">
              Create and organize events for your community
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog
            open={isTemplateDialogOpen}
            onOpenChange={setIsTemplateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button variant="outline">Fill using template</Button>
            </DialogTrigger>
            <DialogContent
              className="max-w-3xl"
              aria-describedby="dialog-description"
            >
              <DialogHeader>
                <DialogTitle>Select Opportunity Template</DialogTitle>
                <DialogDescription id="dialog-description">
                  Choose a template to pre-fill the event form
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search opportunities..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {opportunitiesList.map((opportunity) => (
                      <Card
                        key={opportunity.id}
                        className="cursor-pointer hover:bg-accent"
                        onClick={() => handleTemplateSelect(opportunity)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold">
                                {opportunity.name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {opportunity.community_need}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {opportunity.sdg_alignment?.map((sdg) => (
                                  <Badge key={sdg} variant="secondary">
                                    {sdg}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            {getFormatIcon(opportunity.type)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {hasNextPage && (
                      <div ref={loadMoreRef} className="py-4">
                        <Button
                          variant="ghost"
                          className="w-full"
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                        >
                          {isFetchingNextPage ? "Loading..." : "Load more"}
                        </Button>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={() => form.reset()}>
            Reset
          </Button>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <input type="hidden" {...form.register("opportunity_id")} />
          <div className="grid gap-6 md:grid-cols-6">
            {/* Main Form */}
            <Card className="md:col-span-4">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Event Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter event title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <div className="flex gap-2 items-end">
                          <FormControl>
                            <Textarea
                              placeholder="Describe your event..."
                              className="resize-none"
                              {...field}
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-10"
                            onClick={() => {
                              setPolishFieldLabel("Description");
                              setPolishTargetField("description");
                              setPolishOriginalText(field.value);
                              setShowPolishDialog(true);
                            }}
                            disabled={!field.value || !field.value.trim()}
                          >
                            Polish with AI
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Event Date</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="startTime"
                      render={({ field }) => {
                        // Ensure field.value is always a string
                        const value =
                          typeof field.value === "string" ? field.value : "";

                        return (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select start time">
                                    {value
                                      ? formatTimeForDisplay(value)
                                      : "Select start time"}
                                  </SelectValue>
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {Array.from({ length: 24 * 2 }).map(
                                  (_, index) => {
                                    const hour = Math.floor(index / 2);
                                    const minute = (index % 2) * 30;
                                    const time = `${hour
                                      .toString()
                                      .padStart(2, "0")}:${minute
                                      .toString()
                                      .padStart(2, "0")}`;
                                    const period = hour >= 12 ? "PM" : "AM";
                                    const hour12 =
                                      hour === 0
                                        ? 12
                                        : hour > 12
                                        ? hour - 12
                                        : hour;
                                    const displayTime = `${hour12}:${minute
                                      .toString()
                                      .padStart(2, "0")} ${period}`;

                                    return (
                                      <SelectItem key={time} value={time}>
                                        {displayTime}
                                      </SelectItem>
                                    );
                                  }
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duration</FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            defaultValue={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select duration" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {[
                                0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6,
                                6.5, 7, 7.5, 8,
                              ].map((hours) => (
                                <SelectItem
                                  key={hours}
                                  value={hours.toString()}
                                >
                                  {hours === 0.5
                                    ? "30 minutes"
                                    : hours === 1
                                    ? "1 hour"
                                    : `${hours} hours`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            How long the event will last
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="isVirtual"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <Label>Virtual Event</Label>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {form.watch("isVirtual")
                              ? "Meeting Link"
                              : "Location"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={
                                form.watch("isVirtual")
                                  ? "Enter meeting link"
                                  : "Enter venue address"
                              }
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="maxParticipants"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Maximum Participants</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              {...field}
                              onWheel={(e) => e.currentTarget.blur()}
                              onChange={(e) =>
                                field.onChange(Number(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormDescription>
                            Maximum number of participants that can register for
                            this event
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="prerequisites"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prerequisites</FormLabel>
                          <div className="flex gap-2 items-end">
                            <FormControl>
                              <Textarea
                                placeholder="List any prerequisites for participants (optional)"
                                className="resize-none"
                                {...field}
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="secondary"
                              className="h-10"
                              onClick={() => {
                                setPolishFieldLabel("Prerequisites");
                                setPolishTargetField("prerequisites");
                                setPolishOriginalText(field.value);
                                setShowPolishDialog(true);
                              }}
                              disabled={!field.value.trim()}
                            >
                              Polish with AI
                            </Button>
                          </div>
                          <FormDescription>
                            List any requirements or prerequisites for
                            participants. Separate multiple by new lines.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="registrationDeadline"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Registration Deadline</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <Clock className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date > form.getValues("date") ||
                                date < new Date()
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="sdgs"
                      render={({ field }) => {
                        console.log("SDGs field value:", field.value);
                        console.log("SDGs enum data:", enumData?.data.sdg);
                        return (
                          <FormItem>
                            <FormLabel>SDGs</FormLabel>
                            <FormControl>
                              <Popover open={openSDG} onOpenChange={setOpenSDG}>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openSDG}
                                    className="w-full justify-between"
                                  >
                                    {field.value?.length
                                      ? `${field.value.length} selected`
                                      : "Select SDGs..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0">
                                  <Command>
                                    <CommandInput placeholder="Search SDGs..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        No results found.
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {enumData?.data.sdg.map((sdg) => {
                                          const isSelected =
                                            field.value?.includes(sdg.id);
                                          return (
                                            <CommandItem
                                              key={sdg.id}
                                              onSelect={() => {
                                                const values: string[] =
                                                  field.value || [];
                                                const newValues =
                                                  values.includes(sdg.id)
                                                    ? values.filter(
                                                        (v: string) =>
                                                          v !== sdg.id
                                                      )
                                                    : [...values, sdg.id];
                                                field.onChange(newValues);
                                              }}
                                            >
                                              <div
                                                className={cn(
                                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                  isSelected
                                                    ? "bg-primary text-primary-foreground"
                                                    : "opacity-50 [&_svg]:invisible"
                                                )}
                                              >
                                                <Check className="h-4 w-4" />
                                              </div>
                                              <span className="text-sm">
                                                {sdg.value}
                                              </span>
                                            </CommandItem>
                                          );
                                        })}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            {field.value?.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {field.value?.map((sdgId) => {
                                  const sdgValue = enumData?.data.sdg.find(
                                    (s) => s.id === sdgId
                                  );
                                  return sdgValue ? (
                                    <Badge
                                      key={sdgValue.id}
                                      variant="secondary"
                                      className="bg-blue-100 hover:bg-blue-200 text-blue-700"
                                    >
                                      {sdgValue.value}
                                      <button
                                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            const newValue =
                                              field.value?.filter(
                                                (id) => id !== sdgId
                                              ) || [];
                                            field.onChange(newValue);
                                          }
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          const newValue =
                                            field.value?.filter(
                                              (id) => id !== sdgId
                                            ) || [];
                                          field.onChange(newValue);
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            )}
                            <FormDescription>
                              Select one or more Sustainable Development Goals
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="beneficiaryTypes"
                      render={({ field }) => {
                        console.log("Beneficiary field value:", field.value);
                        console.log(
                          "Beneficiary enum data:",
                          enumData?.data.opportunityBeneficiaryType
                        );
                        return (
                          <FormItem>
                            <FormLabel>Beneficiary Types</FormLabel>
                            <FormControl>
                              <Popover
                                open={openBeneficiary}
                                onOpenChange={setOpenBeneficiary}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openBeneficiary}
                                    className="w-full justify-between"
                                  >
                                    {field.value?.length
                                      ? `${field.value.length} selected`
                                      : "Select beneficiary types..."}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0">
                                  <Command>
                                    <CommandInput placeholder="Search beneficiary types..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        No results found.
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {enumData?.data.opportunityBeneficiaryType.map(
                                          (type) => (
                                            <CommandItem
                                              key={type}
                                              onSelect={() => {
                                                const values =
                                                  field.value || [];
                                                const newValues =
                                                  values.includes(type)
                                                    ? values.filter(
                                                        (v) => v !== type
                                                      )
                                                    : [...values, type];
                                                field.onChange(newValues);
                                              }}
                                            >
                                              <div
                                                className={cn(
                                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                                  field.value?.includes(type)
                                                    ? "bg-primary text-primary-foreground"
                                                    : "opacity-50 [&_svg]:invisible"
                                                )}
                                              >
                                                <Check className="h-4 w-4" />
                                              </div>
                                              <span className="text-sm">
                                                {type}
                                              </span>
                                            </CommandItem>
                                          )
                                        )}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            {field.value?.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {field.value?.map((typeValue) => {
                                  const type =
                                    enumData?.data.opportunityBeneficiaryType.find(
                                      (t) => t === typeValue
                                    );
                                  return type ? (
                                    <Badge
                                      key={type}
                                      variant="secondary"
                                      className="bg-green-100 hover:bg-green-200 text-green-700"
                                    >
                                      {type}
                                      <button
                                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            const newValue =
                                              field.value?.filter(
                                                (id) => id !== typeValue
                                              ) || [];
                                            field.onChange(newValue);
                                          }
                                        }}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          const newValue =
                                            field.value?.filter(
                                              (v) => v !== typeValue
                                            ) || [];
                                          field.onChange(newValue);
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            )}
                            <FormDescription>
                              Select one or more beneficiary types
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="eventType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Event Type</FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openSDG}
                                  className="w-full justify-between"
                                >
                                  {field.value || "Select event type"}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0">
                                <Command>
                                  <CommandInput placeholder="Search event types..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No results found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {enumData?.data.opportunityType.map(
                                        (type) => (
                                          <CommandItem
                                            key={type}
                                            onSelect={() =>
                                              field.onChange(type)
                                            }
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4",
                                                field.value === type
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                            {type}
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormDescription>
                            Select the type of event
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="images"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Event Image</FormLabel>
                        <FormControl>
                          <div className="space-y-4">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleImageUpload(file);
                                }
                              }}
                              disabled={uploadingImage}
                            />
                            {uploadingImage && (
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <p className="text-sm text-muted-foreground">
                                  Uploading image...
                                </p>
                              </div>
                            )}
                            {previewImage && (
                              <div className="relative aspect-video w-full max-w-sm overflow-hidden rounded-lg border">
                                <img
                                  src={previewImage}
                                  alt="Preview"
                                  className="h-full w-full object-cover"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute right-2 top-2"
                                  onClick={() => {
                                    form.setValue("images", []);
                                    setPreviewImage(null);
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormDescription>
                          Upload an image for your event. Recommended size:
                          1200x630 pixels
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Button
                        variant="outline"
                        className="h-32"
                        onClick={() => {
                          // TODO: Implement image upload
                        }}
                      >
                        <ImagePlus className="h-8 w-8 text-muted-foreground" />
                      </Button>
                    )}
                  </div>

                  <ScrollArea className="h-[500px] rounded-md border p-4">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">
                        {form.watch("title") || "Event Title"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {form.watch("description") ||
                          "Event description will appear here"}
                      </p>

                      {form.watch("date") && (
                        <div className="flex items-center gap-2 text-sm">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{format(form.watch("date"), "PPP")}</span>
                          {form.watch("startTime") && (
                            <span>at {form.watch("startTime")}</span>
                          )}
                        </div>
                      )}

                      {form.watch("location") && (
                        <p className="text-sm">
                          {form.watch("isVirtual")
                            ? "Meeting Link: "
                            : "Location: "}
                          {form.watch("location")}
                        </p>
                      )}

                      {form.watch("maxParticipants") && (
                        <p className="text-sm">
                          Capacity: {form.watch("maxParticipants")} participants
                        </p>
                      )}

                      {form.watch("prerequisites") && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm">
                            Prerequisites:
                          </h4>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {form
                              .watch("prerequisites")
                              .split("\n")
                              .filter(Boolean)
                              .map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard/community-events")}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={form.formState.isSubmitting || uploadingImage}
              onClick={handleSubmitButtonClick}
            >
              {form.formState.isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isEditMode ? "Updating..." : "Creating..."}
                </div>
              ) : (
                <span>{isEditMode ? "Update Event" : "Create Event"}</span>
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* Polish With AI Dialog (only for description) */}
      <PolishWithAIDialog
        open={showPolishDialog}
        originalText={polishOriginalText}
        fieldLabel={polishFieldLabel}
        session={session}
        context={
          polishTargetField === "description"
            ? `This is a community event description. 
               Event Title: ${form.getValues("title")}
               Event Type: ${form.getValues("eventType")}
               SDGs: ${form.getValues("sdgs")?.join(", ") || "Not specified"}
               Make the description engaging and informative.`
            : polishTargetField === "prerequisites"
            ? `This is a list of prerequisites for a community event.
               Event Title: ${form.getValues("title")}
               Event Type: ${form.getValues("eventType")}
               SDGs: ${form.getValues("sdgs")?.join(", ") || "Not specified"}
               
               Create a clear, concise list of prerequisites that volunteers should know or bring. Consider:
               - Any required skills or experience
               - Items volunteers need to bring
               - Dress code or physical requirements
               - Any age restrictions or certifications needed
               - Technology or tools required for virtual events
               
               Format in new line for easy reading without any preceeding - or * .`
            : ""
        }
        onAccept={(polishedText) => {
          if (polishTargetField === "description") {
            form.setValue("description", polishedText, {
              shouldValidate: true,
            });
          } else if (polishTargetField === "prerequisites") {
            form.setValue("prerequisites", polishedText, {
              shouldValidate: true,
            });
          }
          setShowPolishDialog(false);
        }}
        onCancel={() => setShowPolishDialog(false)}
      />
    </div>
  );
}
